import { useMutation, useQueryClient } from "@tanstack/react-query";
import { gustoContract } from "@common/contracts/gusto.contract";
import { gustoIntegrationContract } from "@common/contracts/gusto-integration.contract";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useScope } from "@state/scopeContext.lib";
import { useConnectTarget } from "@hooks/useConnectTarget";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { IntegrationConnectionDto } from "@common/dto/integrationConnection/integration-connection.dto";

const gustoContractClient = getBackendAPIClient(gustoContract);
const gustoIntegrationContractClient = getBackendAPIClient(
    gustoIntegrationContract,
);

export function useCreateGustoAccountMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async () => {
            if (!workspaceId) {
                return undefined;
            }

            const response = await gustoContractClient.createGustoAccount({
                body: {},
                params: { workspaceId },
            });

            return response.body.accountClaimUrl;
        },
    });
}

export function useReconcileGustoTransfersMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            gustoAccountId,
            transactionIds,
        }: {
            gustoAccountId: number;
            transactionIds: number[];
        }) =>
            await gustoContractClient.reconcileTransfers({
                body: { gustoAccountId, transactionIds },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["transactions"],
            });
        },
    });
}

export function useCreateGustoConnectionMutation(
    onSuccessCallback: (connection?: IntegrationConnection) => void,
) {
    const { workspaceId, isOrgConnect, refreshConnections } =
        useConnectTarget();
    return useMutation({
        mutationFn: async ({
            code,
            defaultEntityId,
        }: {
            code: string;
            defaultEntityId?: number;
        }) => {
            if (!workspaceId && !isOrgConnect) {
                return undefined;
            }

            const result =
                await gustoIntegrationContractClient.createConnection({
                    body: { code, defaultEntityId, workspaceId },
                });
            return IntegrationConnectionDto.deserialize(result.body);
        },
        onSuccess: async (connection) => {
            await refreshConnections(
                connection && {
                    provider: "gusto",
                    connectionId: String(connection.id),
                },
            );
            onSuccessCallback(connection);
        },
        mutationKey: ["gusto-connection-creation"],
    });
}
